import { useContext, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { BadgeAlert, TriangleAlert } from 'lucide-react';
import { AppContext } from "../../AppContext";

import Assessment from '@mui/icons-material/Assessment';
import Schema from '@mui/icons-material/Schema';

import Alert from './../ui/Alert';
import DataLoading from './../DataLoading';

import { PowerBiReport, PowerBiDataset } from '../../models/models';
import useAppAccount from '../../auth/useAppAccount';

const WELCOME = "Welcome to the Mikano International Analytics Portal";

const Home = () => {
  const { isAuthenticated, isManagement, displayName, loginId } = useAppAccount();
  const navigate = useNavigate();
  const { embeddingData } = useContext(AppContext);
  const [activationStatus, setActivationStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleActivateWorkspace = async () => {
    setActivationStatus('sending');
    try {
      const response = await fetch("https://mikanoautomation.app.n8n.cloud/webhook/mk_report_update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ email: loginId })
      });
      if (response.ok) {
        setActivationStatus('success');
      } else {
        setActivationStatus('error');
      }
    } catch (error) {
      console.error("Error activating workspace:", error);
      setActivationStatus('error');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className=" px-4 font-light text-white ">
        <h2 className="my-6 text-2xl">{WELCOME}</h2>
        <p className="mb-6">
          This platform serves as the centralized hub for managing Mikano's data environment and embedded Power BI reporting. It streamlines the provisioning of departmental workspaces, automates dashboard integrations, and allows administrators to securely assign access permissions across the organization.
        </p>
       <div className='flex gap-x-2 p-4 w-1/3  bg-themedaccent/20  rounded-md '>
         <BadgeAlert /> <p>Click the <strong>sign in</strong> link in the upper right to access your reports.</p>
       </div>
      </div>
    )
  }

  if (isAuthenticated && embeddingData.user && !embeddingData.tenantName) {
    return (
      <div className="px-4 text-white">
        <h2 className="my-6 text-2xl">{WELCOME}</h2>
        <div className='flex flex-col gap-y-4 p-6 bg-themednavyblue_200 border border-themedgrey/30 rounded-lg max-w-2xl'>
          <div className='flex gap-x-2 text-yellow-500'>
            <TriangleAlert className="shrink-0" />
            <p className="text-sm font-light leading-relaxed">
              Your user account has not been assigned to a department workspace. You will
              not have access to any reports until your user account has been assigned.
            </p>
          </div>
          <div className="mt-2">
            {isManagement && (
              <p className="text-sm">Contact the admin to assign reports to the Management account.</p>
            )}
            {!isManagement && activationStatus === 'idle' && (
              <button
                type="button"
                onClick={handleActivateWorkspace}
                className="px-4 py-2 bg-themedaccent hover:bg-themedaccent/80 text-white text-sm font-medium rounded transition duration-200"
              >
                Activate your workspace
              </button>
            )}
            {!isManagement && activationStatus === 'sending' && (
              <button
                type="button"
                disabled
                className="px-4 py-2 bg-themedgrey/50 text-white text-sm font-medium rounded cursor-not-allowed"
              >
                Sending request...
              </button>
            )}
            {!isManagement && activationStatus === 'success' && (
              <div className="text-green-500 text-sm font-medium">
                Workspace activation request sent! The admin will be notified.
              </div>
            )}
            {!isManagement && activationStatus === 'error' && (
              <div className="flex flex-col gap-y-2">
                <div className="text-red-500 text-sm font-medium">
                  Failed to send request. Please try again.
                </div>
                <button
                  type="button"
                  onClick={handleActivateWorkspace}
                  className="w-fit px-4 py-2 bg-themedaccent hover:bg-themedaccent/80 text-white text-sm font-medium rounded transition duration-200"
                >
                  Retry request
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (embeddingData.workspaceArtifactsLoading) {
    return <DataLoading />
  }

  const sessionRows: [string, string, boolean?][] = [
    ["User Login:", loginId, true],
    ["User Display Name:", displayName, true],
    ["Tenant Name:", embeddingData.tenantName, true],
    ["User can edit content:", String(embeddingData.userCanEdit)],
    ["User can create content:", String(embeddingData.userCanCreate)],
  ];

  const contentList = (items: (PowerBiReport | PowerBiDataset)[] | null, Icon: typeof Assessment) => (
    <ul className="list-none border border-[#CCCCCC]  p-0">
      {items?.map((item) => (
        <li key={item.id} className=" border-b border-gray-300 last:border-b-0">
          <button
            type="button"
            onClick={() => { navigate("/reports/" + item.id); }}
            className="flex w-full items-center gap-3 px-3 py-1.5 text-left hover:bg-themedaccent/20"
          >
            <span className="flex h-9 w-9 shrink-0 items-center 
            justify-center rounded-full bg-themedgrey text-white">
              <Icon fontSize="small" />
            </span>
            <span className="text-sm">{item.name}</span>
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="mx-auto w-full max-w-screen-xl px-4">
      <h2 className="my-6 text-2xl">{WELCOME}</h2>

      <Alert severity="info">
        Now that you have logged in, you can use the left navigation menu to navigate to the reports accessible within your department.
      </Alert>

      <h4 className="mt-4 mb-1.5 text-lg font-medium">Login Session Info:</h4>

      <div className="border border-gray-300 ]">
        <table className="w-full min-w-[650px] border-collapse text-sm">
          <tbody>
            {sessionRows.map(([label, value]) => (
              <tr key={label} className="border-b border-gray-300 last:border-b-0">
                <td className="w-[200px] px-3 py-1.5 align-top">{label}</td>
                <td className="px-3 py-1.5"><strong>{value}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4 className="mt-4 mb-2 border-b border-gray-400 text-lg font-medium">Tenant Contents</h4>

      <div className="flex">
        <div className={embeddingData.userCanCreate ? "w-1/2 pr-1" : "w-full"}>
          <div className="rounded-t bg-themedaccent/20 px-3 py-1 text-xl text-white">Reports</div>
          {embeddingData.reports && contentList(embeddingData.reports, Assessment)}
        </div>
        {embeddingData.userCanCreate && (
          <div className="w-1/2 pl-1">
            <div className="rounded-t bg-themedaccent/20 px-3 py-1 text-xl text-white">Datasets</div>
            {embeddingData.datasets && contentList(embeddingData.datasets, Schema)}
          </div>
        )}
      </div>
    </div>
  )
}

export default Home;
